





































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import {
  RecurrenceSettingPayload,
  DataCollectionPeriodDTO,
  ParticipantListDTO,
  ProjectCriteria,
  ProjectSite,
  ProjectDetailPayload,
} from '@/store/modules/projects/types/projects.types';
import NewAuditSettings from './NewAuditSettings.vue';
import moment from 'moment';
import { getStartDateTime, getEndDateTime } from '../../../utils/date.util';
import { RootState } from '@/store/store';
import { PacesParticipantRoles } from '@/jbi-shared/types/paces-project.types';
import {
  AuditSamplingType,
  RecurrenceType,
} from '@/jbi-shared/types/project.types';
import { AuditCriteria } from '@/store/modules/audits/types/audits.types';
import BaseLoading from '@/components/base/BaseLoading.vue';
import { ApiState } from '../../../store/types/general.types';

@Component({ components: { NewAuditSettings, BaseLoading } })
export default class NewAuditModal extends Vue {
  @Prop()
  public modalTitle!: string;
  @Prop()
  public projectDetail!: ProjectDetailPayload;
  @Prop()
  public latestDate!: Date;
  @Prop()
  public auditCount!: number;
  @Prop()
  public latestAuditId!: number | undefined;

  public selectedCriteria: ProjectCriteria[] = [];
  public selectedSites: ProjectSite[] = [];

  public defaultEndDate: any = moment(new Date()).add(1, 'M');
  public dataCollectionPeriodDateTotal: DataCollectionPeriodDTO = {
    auditNumber: 1,
    auditName: this.auditName,
    startDate: getStartDateTime(this.latestDate),
    endDate: getEndDateTime(this.defaultEndDate.subtract(1, 'd')),
    auditCriteriaIds: [],
    auditSiteIds: [],
    auditParticipants: [],
  };
  public auditSetting: RecurrenceSettingPayload = {
    recurrenceType: RecurrenceType.ONE_OFF,
    multipleRecurrenceType: null,
    auditSamplingType: AuditSamplingType.TARGET_SAMPLE_SIZE,
    interval: 1,
    auditNumber: 1,
    scheduledRecurrenceStartDate: getStartDateTime(this.latestDate),
    scheduledRecurrenceEndDate: null,
    enableGrip: true,
    dataCollectionPeriodSettings: [
      {
        ...this.dataCollectionPeriodDateTotal,
      },
    ],
  };
  public showNewDataCollectionPeriod: boolean = false;
  public isAuditCycleDataValidForAllCycles: boolean = false;
  public isLoading: boolean = false;

  @Action('projects/getProjectCriteria')
  public getProjectCriteria!: (projectId: number) => void;

  @State((state: RootState) => state.projects.apiState.getProjectCriteria)
  public getProjectCriteriaState!: ApiState;

  @State((state) => state.projects.projectCriteria)
  public projectCriteria!: ProjectCriteria[];

  @Action('projects/getProjectSites')
  public getProjectSites!: (projectId: number) => void;

  @State((state: RootState) => state.projects.apiState.getProjectSites)
  public getProjectSitesState!: ApiState;

  @State((state) => state.projects.projectSites)
  public projectSites!: ProjectSite[];

  @Action('projects/getProjectParticipants')
  public getProjectParticipants!: (payload: {
    projectId: number;
    includeInvites: boolean;
  }) => void;

  @State((state: RootState) => state.projects.apiState.getProjectParticipants)
  public getProjectParticipantsState!: ApiState;

  @State((state: RootState) => state.projects.participants)
  public projectParticipants!: ParticipantListDTO[];

  @Action('audits/getAuditCriteria')
  public getAuditCriteria!: (auditId: number) => void;

  @State((state) => state.audits.auditCriteria)
  public auditCriteria!: AuditCriteria[];

  get auditName(): string {
    return this.auditCount !== 0
      ? 'Follow-Up ' + Number(this.auditCount)
      : 'Baseline';
  }

  /** Formats criteria to include only the required fields: title and uniqueCriteriaId. */
  get formattedCriteria() {
    return this.projectCriteria
      ? this.projectCriteria.map((criteria) => {
          return {
            title: criteria.documentId
              ? `${criteria.documentId} || ${criteria.title}`
              : criteria.title,
            uniqueCriteriaId: criteria.id,
          };
        })
      : [];
  }

  /** Formats sites to include only the required fields: name and id. */
  get formattedSites() {
    return this.projectSites
      ? this.projectSites.map((projectSite) => {
          return {
            id: projectSite.site.id,
            name: projectSite.site.name,
          };
        })
      : [];
  }

  /**
   * Filters participants based on role (as Data Collector) and project level,
   * where project level is determined by the absence of an audit (!audit).
   * Formats the result to include only the required fields: email and siteIds.
   */
  get formattedDataCollectors() {
    return this.projectParticipants
      ? this.projectParticipants
          // Filter to only include "Data Collector" roles
          .filter(
            (participant) =>
              participant.participantRole ===
                PacesParticipantRoles.DataCollector && !participant.audit,
          )
          .map((participant) => {
            return {
              email: participant.userEmail,
              siteIds: participant.participantSites.map((ps) => ps.site),
            };
          })
      : [];
  }

  public handleCycleDataForm(isValid: boolean) {
    this.isAuditCycleDataValidForAllCycles = isValid;
  }

  get prevAuditCriteriaIds() {
    return this.auditCriteria && this.auditCriteria.length
      ? this.auditCriteria.map((ac) => ac.criteria.id)
      : [];
  }

  public mounted() {
    this.isLoading = true;

    // Fetch all required data from APIs before rendering options
    this.getProjectCriteria(this.projectDetail.id);
    this.getProjectSites(this.projectDetail.id);
    this.getProjectParticipants({
      projectId: this.projectDetail.id,
      includeInvites: true,
    });

    if (this.latestAuditId) {
      this.getAuditCriteria(this.latestAuditId);
    }

    // Show the audit settings form to the user
    this.showNewDataCollectionPeriod = true;
  }

  public addNewAuditSetting() {
    if (this.auditSetting.scheduledRecurrenceStartDate) {
      this.auditSetting.scheduledRecurrenceStartDate = getStartDateTime(
        this.auditSetting.scheduledRecurrenceStartDate,
      );
    }
    this.$emit('addNewAudit', this.auditSetting);
    this.closeModal();
  }

  public handleAuditSettingUpdate(newValue: RecurrenceSettingPayload) {
    if (newValue.scheduledRecurrenceStartDate) {
      newValue.scheduledRecurrenceStartDate =
        newValue.scheduledRecurrenceStartDate;
    }
    if (newValue.scheduledRecurrenceEndDate) {
      newValue.scheduledRecurrenceEndDate = newValue.scheduledRecurrenceEndDate;
    }
    this.auditSetting = newValue;
  }

  public handleSelectionsUpdate({
    criteria,
    sites,
  }: {
    criteria: ProjectCriteria[];
    sites: ProjectSite[];
  }) {
    this.selectedCriteria = criteria;
    this.selectedSites = sites;
  }

  public closeModal() {
    this.$emit('close');
  }

  public isAuditIntervalInValid(): boolean {
    if (
      this.auditSetting.interval !== undefined &&
      this.auditSetting.interval < 1
    ) {
      return true;
    }
    return false;
  }

  public isAuditNumberInValid(): boolean {
    if (
      this.auditSetting.auditNumber !== undefined &&
      (this.auditSetting.auditNumber < 1 || this.auditSetting.auditNumber > 10)
    ) {
      return true;
    }
    return false;
  }

  get validateForm() {
    if (
      this.auditSetting.recurrenceType === RecurrenceType.MULTIPLE &&
      (this.isAuditIntervalInValid() || this.isAuditNumberInValid()) &&
      !this.isAuditCycleDataValidForAllCycles
    ) {
      return true;
    }

    if (this.selectedCriteria.length === 0 || this.selectedSites.length === 0) {
      return true;
    }

    return false;
  }

  public checkStateOfData() {
    if (
      this.getProjectCriteriaState.loading ||
      this.getProjectSitesState.loading ||
      this.getProjectParticipantsState.loading
    ) {
      this.isLoading = true;
    } else if (
      this.getProjectCriteriaState.success &&
      this.getProjectSitesState.success &&
      this.getProjectParticipantsState.success
    ) {
      this.isLoading = false;
    }
  }

  @Watch('getProjectCriteriaState', { deep: true })
  public onWatchProjectCriteriaStates() {
    this.checkStateOfData();
  }

  @Watch('getProjectSitesState', { deep: true })
  public onWatchProjectSitesStates() {
    this.checkStateOfData();
  }

  @Watch('getProjectParticipantsState', { deep: true })
  public onWatchProjectParticipantsStates() {
    this.checkStateOfData();
  }
}
